import {
  escapeRegExp,
  toFlexibleSeparatorPattern,
  toLowerCaseSet,
} from './groupIdPattern';
import type { ILevelConfig } from './types';

const getSegmentKeys = (levelsConfig: ILevelConfig[]) =>
  Array.from(
    new Set(
      levelsConfig.flatMap((level) =>
        level.promoRewardType === 'bets' && level.segments
          ? Object.keys(level.segments)
          : []
      )
    )
  );

const findSegmentByGroup = (
  levelsConfig: ILevelConfig[],
  segmentKeys: string[],
  statusIds: string[]
) =>
  segmentKeys.find((key) =>
    levelsConfig.some((level) => {
      const groupId =
        level.promoRewardType === 'bets' && level.segments?.[key]?.groupId;
      return !!groupId && statusIds.includes(groupId.toLowerCase());
    })
  );

const findSegmentByStatus = (
  levelsConfig: ILevelConfig[],
  segmentKeys: string[],
  statusIds: string[]
) => {
  const keysPattern = segmentKeys.map(escapeRegExp).join('|');

  for (const level of levelsConfig) {
    const pattern = new RegExp(
      `^${toFlexibleSeparatorPattern(level.name)}[ _]\\d+%?[ _](${keysPattern})$`,
      'i'
    );

    for (const id of statusIds) {
      const match = pattern.exec(id);
      if (match)
        return segmentKeys.find(
          (key) => key.toLowerCase() === match[1].toLowerCase()
        );
    }
  }
};

// Statuses can be gone once every level is paid out, so a reward bonus that
// carries a segment's groupKey identifies the segment as well.
const findSegmentByReward = (
  levelsConfig: ILevelConfig[],
  segmentKeys: string[],
  rewardGroupKeys: Set<string | undefined>
) =>
  segmentKeys.find((key) =>
    levelsConfig.some((level) => {
      const rewardGroupKey =
        level.promoRewardType === 'bets' &&
        level.segments?.[key]?.rewardGroupKey;
      return (
        !!rewardGroupKey && rewardGroupKeys.has(rewardGroupKey.toLowerCase())
      );
    })
  );

export const applyLevelSegment = (
  levelsConfig: ILevelConfig[],
  statusIds: string[],
  rewardGroupKeys: Set<string>
): ILevelConfig[] => {
  const segmentKeys = getSegmentKeys(levelsConfig);
  if (!segmentKeys.length) return levelsConfig;

  const lowerStatusIds = statusIds.map((id) => id.toLowerCase());
  const segment =
    findSegmentByGroup(levelsConfig, segmentKeys, lowerStatusIds) ??
    findSegmentByStatus(levelsConfig, segmentKeys, lowerStatusIds) ??
    findSegmentByReward(
      levelsConfig,
      segmentKeys,
      toLowerCaseSet(rewardGroupKeys)
    );
  if (!segment) return levelsConfig;

  return levelsConfig.map((level) => {
    if (level.promoRewardType !== 'bets' || !level.segments?.[segment])
      return level;

    const { groupId: _, ...segmentData } = level.segments[segment];
    return { ...level, ...segmentData };
  });
};
